import { useIntl } from 'react-intl'
import { useCallback } from 'react'
import { useHistory } from 'react-router-dom'
import { DndContext } from '@dnd-kit/core'
import {
  restrictToParentElement,
  restrictToVerticalAxis
} from '@dnd-kit/modifiers'
import TableComponent from '../../../../components/stories/organisms/table'
import TableToolbar from '../../../../components/stories/organisms/table/components/toolbar'
import {
  PlanMenuTableContainer,
  PlanMenuTableWrapper,
  PlanMenuTableScrollContainer
} from '../../styled-component'
import ShowInactiveSwitch from '../../../../components/show-inactive-switch'
import PlanMenuTableBody from './components/plan-menu-table-body'
import PlanMenuTableContent from './components/plan-menu-table-content'
import PlanMenuBulkContextMenu from '../plan-menu-bulk-context-menu'
import PlanMenuTablePagination from './components/plan-menu-table-pagination'
import { usePlanMenuTable } from './hooks/use-plan-menu-table'
import { planPermissionOptions } from '../../constants'

// every drop target is a row stacked below another one, so sideways movement
// buys nothing — and keeping the dragged row inside the list means its transform
// never extends the scroll area, which is what made the card grow while dragging
const planMenuDragModifiers = [restrictToVerticalAxis, restrictToParentElement]

const PlanMenuList = () => {
  const { formatMessage, locale } = useIntl()
  const history = useHistory()

  const {
    childrenByFolderId,
    clearRowSelection,
    dnd,
    folderChildrenStateById,
    isError,
    pagination,
    pageSize,
    rowSelection,
    rowsById,
    search,
    selectedItems,
    setPageSize,
    setSearch,
    setShowInactive,
    showInactive,
    table,
    userPermissions,
    projectId
  } = usePlanMenuTable({ formatMessage, locale })

  const canCreatePlans = userPermissions.includes(
    planPermissionOptions.CREATE_PLAN
  )

  const hasNoRows = table.getRowModel().rows.length === 0
  // filtering makes no sense with nothing to filter, but a search that returned
  // nothing must stay editable so the user can clear it
  const hasNoPlans = hasNoRows && !search

  const handleOpenCreatePlanModal = useCallback(() => {
    history.push(`/project/${projectId}/import`)
  }, [history])

  return (
    <PlanMenuTableContainer $isEmpty={hasNoRows}>
      {/* the toolbar sits outside the table card on purpose: per design it has
          no background of its own */}
      <TableToolbar.Root>
        <TableToolbar.LeftContainer>
          <TableToolbar.Search
            value={search}
            onChangeSearch={setSearch}
            defaultDesktopVariantOpen={!!search}
            disabled={hasNoPlans}
          />
          <ShowInactiveSwitch
            checked={showInactive}
            onCheckedChange={setShowInactive}
            disabled={hasNoPlans && !showInactive}
            label={formatMessage({ id: 'plans_overview_show-inactive' })}
          />
        </TableToolbar.LeftContainer>
        <TableToolbar.RightContainer>
          <TableToolbar.PageSize
            onChangePageSize={setPageSize}
            pageSize={pageSize}
            disabled={hasNoPlans}
          />
          <PlanMenuBulkContextMenu
            onBatchComplete={clearRowSelection}
            selectedItems={selectedItems}
          />
        </TableToolbar.RightContainer>
      </TableToolbar.Root>

      <PlanMenuTableWrapper $isEmpty={hasNoRows}>
        <TableComponent.Root>
          <PlanMenuTableScrollContainer>
            <DndContext
              collisionDetection={dnd.collisionDetection}
              modifiers={planMenuDragModifiers}
              onDragEnd={dnd.handleDragEnd}
              sensors={dnd.sensors}
            >
              <PlanMenuTableContent
                rowSelection={rowSelection}
                rowsById={rowsById}
                table={table}
              >
                <PlanMenuTableBody
                  canCreatePlans={canCreatePlans}
                  childrenByFolderId={childrenByFolderId}
                  folderChildrenStateById={folderChildrenStateById}
                  formatMessage={formatMessage}
                  isError={isError}
                  movingItemIds={dnd.movingItemIds}
                  onCreatePlan={handleOpenCreatePlanModal}
                  rowSelection={rowSelection}
                  rowsById={rowsById}
                  table={table}
                  search={search}
                />
              </PlanMenuTableContent>
            </DndContext>
          </PlanMenuTableScrollContainer>
        </TableComponent.Root>
      </PlanMenuTableWrapper>

      <PlanMenuTablePagination
        pagination={pagination}
        rowSelection={rowSelection}
      />
    </PlanMenuTableContainer>
  )
}

export default PlanMenuList
